import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'
import { match } from 'ts-pattern'

import { HStack } from '~/components/elements/HStack'
import {
  ItemSelector,
  type ItemSelectorProps,
} from '~/components/elements/ItemSelector'
import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import { VStack } from '~/components/elements/VStack'
import { CustomerAttributeGroupSelector } from '~/components/features/CustomerAttributeGroupSelector'
import { QuestionTreeSelector } from '~/components/features/QuestionTreeSelector'
import { useFormField } from '~/hooks/useFormField'
import { useSurveyQueryQuestions } from '~/hooks/useSurveyQuery'
import { compact, findByPath } from '~/utils'

import { useAvailableCustomerAttributeGroups } from '../../hooks/useAvailableCustomerAttributeGroups'

import { type InputValues } from './utils'

type Category = InputValues['filters'][number]['category']

type CategoryItem = {
  id: Category
  label: string
}

export type FilterInputProps = {
  index: number
  onDelete: (index: number) => void
}

export const FilterInput = ({ index, onDelete }: FilterInputProps) => {
  const { t } = useTranslation()
  const selectFromQuestionnaireResponseLabel = t(
    'select_from_questionnaire_response',
  )
  const selectFromCustomizedSegmentsLabel = t('select_from_customized_segments')
  const { control } = useFormContext<InputValues>()
  const categoryField = useFormField(control, `filters.${index}.category`)
  const idField = useFormField(control, `filters.${index}.id`)

  const questions = useSurveyQueryQuestions()
  const customerAttributeGroups = useAvailableCustomerAttributeGroups(undefined)

  const categoryItems = React.useMemo<CategoryItem[]>(
    () =>
      compact([
        0 < questions.length && {
          id: 'Question',
          label: selectFromQuestionnaireResponseLabel,
        },
        0 < customerAttributeGroups.length && {
          id: 'CustomerAttribute',
          label: selectFromCustomizedSegmentsLabel,
        },
      ]),
    [
      customerAttributeGroups.length,
      questions.length,
      selectFromCustomizedSegmentsLabel,
      selectFromQuestionnaireResponseLabel,
    ],
  )

  const handleCategorySelectorChange = React.useCallback<
    EH<ItemSelectorProps, 'onChange'>
  >(
    (item: CategoryItem) => {
      categoryField.onChange(item.id)
      idField.onChange(
        match(item.id)
          .with('Question', () => R.first(questions)?.question_id)
          .with('CustomerAttribute', () => R.first(customerAttributeGroups)?.id)
          .exhaustive(),
      )
    },
    [categoryField, customerAttributeGroups, idField, questions],
  )

  const handleDeleteButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    onDelete(index)
  }, [index, onDelete])

  return (
    <VStack border="light" padding={2} spacing={2}>
      <HStack justify="space-between" spacing={2}>
        <ItemSelector
          data={categoryItems}
          dataItemKey="id"
          textField="label"
          {...categoryField}
          value={findByPath(categoryItems, 'id', categoryField.value)}
          width={160}
          onChange={handleCategorySelectorChange}
        />

        <StyledButton
          buttonStyle="light"
          fillMode="flat"
          icon="x"
          onClick={handleDeleteButtonClick}
        >
          {t('delete_definition')}
        </StyledButton>
      </HStack>

      {match(categoryField.value)
        .with('Question', () => (
          <QuestionTreeSelector questions={questions} {...idField} />
        ))
        .with('CustomerAttribute', () => (
          <CustomerAttributeGroupSelector
            customerAttributeGroups={customerAttributeGroups}
            {...idField}
          />
        ))
        .exhaustive()}
    </VStack>
  )
}
